import BigNumber from 'bignumber.js';

export const AMORTIZATION_LINE_NUMBER_PREDICATE = 'LoanAmortizationLineNumberFeePredicate';

export interface AmortizationLineNumberPredicate {
  type: string;
  value: number;
}

export interface AmortizationSpecificFee {
  balance: number;
  applyPredicates?: AmortizationLineNumberPredicate[] | null;
}

export interface AmortizedFee {
  feeId: number;
  amortizationLineNumber: number;
  balance: number;
}

/**
 * Line number a fee instance is bound to, or null when it is not bound to a single line.
 * Only the predicate's value is compared, matching how the backend associates a fee
 * instance with an amortization line.
 */
export const amortizationLineNumberOf = (fee: AmortizationSpecificFee): number | null => {
  const predicate = (fee.applyPredicates ?? []).find(p => p && p.type === AMORTIZATION_LINE_NUMBER_PREDICATE);
  return predicate ? Number(predicate.value) : null;
};

/**
 * Balance of the fee instances of one fee definition. A fee applied on every loan payment has
 * one instance per amortization line, so when a single line is being waived only the instances
 * bound to that line contribute; passing null sums every instance (whole-loan waive).
 */
export const customFeeBalanceForLine = (fees: AmortizationSpecificFee[], lineNumber: number | null): number =>
  (fees ?? [])
    .filter(fee => lineNumber === null || amortizationLineNumberOf(fee) === lineNumber)
    .reduce((total, fee) => total.plus(fee.balance || 0), new BigNumber(0))
    .toNumber();

/**
 * Balance a fee spread across the schedule holds on one amortization line. A line the fee was
 * not spread over carries none of it.
 */
export const amortizedFeeBalanceForLine = (amortizedFees: AmortizedFee[], feeId: number, lineNumber: number): number => {
  const amortizedFee = (amortizedFees ?? [])
    .find(fee => fee.feeId === feeId && fee.amortizationLineNumber === lineNumber);
  return amortizedFee ? amortizedFee.balance : 0;
};
